// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { UserPlus, Search, X, Calendar, Clock, Plus, Trash2 } from 'lucide-react';
import { AdministrativeFinancialIntegrationService } from '@/services/administrativeFinancialIntegrationService';
import AdvancedFeeConfiguration from '../financial/AdvancedFeeConfiguration';

interface AdministrativeProcessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingCase?: any; // Processo administrativo para edição
}

interface ProcedureType {
  id: number;
  name: string;
  category: string;
}

interface Agency {
  id: number;
  name: string;
  category: string;
}

interface Client {
  id: string;
  name: string;
  email: string;
  cpf?: string | null;
  document?: string | null; // Possível nome alternativo para CPF
  [key: string]: any; // Aceitar outras colunas que possam existir
}

interface Deadline {
  id: string;
  title: string;
  description: string;
  due_date: string;
  priority: 'baixa' | 'normal' | 'alta' | 'urgente';
  type: 'prazo_legal' | 'prazo_interno' | 'prazo_cliente' | 'outro';
  status: 'pendente' | 'em_andamento' | 'concluido' | 'atrasado';
}

interface FinancialInfo {
  clientFees: string;
  courtCosts: string;
  successFeeType: 'percentage' | 'fixed' | 'conditional';
  successFeeValue: string;
  successFeeTrigger: string;
  successFeeConditions: string;
  paymentMethod: 'a_vista' | 'parcelado' | 'condicional' | 'por_fase';
  installmentCount: number;
  installmentFrequency: 'mensal' | 'bimestral' | 'trimestral' | 'semestral' | 'anual';
  firstPaymentDate: string;
  latePaymentInterest: string;
  earlyPaymentDiscount: string;
  paymentConditions: string;
  collectionNotes: string;
  proceduralStrategy: string;
  internalNotes: string;
  fee_configuration?: {
    initial_fee_type: 'fixed' | 'percentage';
    initial_fee_value: number;
    success_fee_type: 'percentage_global' | 'percentage_installment' | 'fixed_global' | 'fixed_monthly';
    success_fee_value: number;
    success_fee_trigger: 'acordo' | 'sentenca' | 'concessao_beneficio' | 'recurso_procedente' | 'outro';
    success_fee_conditions: string;
    payment_method: 'a_vista' | 'parcelado' | 'entrada_parcelas';
    down_payment: number;
    installment_count: number;
    installment_frequency: 'mensal' | 'bimestral' | 'trimestral';
    late_payment_interest: number;
    early_payment_discount: number;
    grace_period_days: number;
    payment_conditions: string;
    collection_notes: string;
  };
}

export default function AdministrativeProcessModal({ isOpen, onClose, onSuccess, editingCase }: AdministrativeProcessModalProps) {
  const { user } = useAuth();
  const [procedureTypes, setProcedureTypes] = useState<ProcedureType[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [showClientList, setShowClientList] = useState(false);
  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  const [isDeadlineModalOpen, setIsDeadlineModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('identification');

  // Sempre abre na primeira aba
  useEffect(() => {
    if (isOpen) setActiveTab('identification');
  }, [isOpen]);
  const [financialInfo, setFinancialInfo] = useState<FinancialInfo>({
    clientFees: '',
    courtCosts: '',
    successFeeType: 'percentage',
    successFeeValue: '',
    successFeeTrigger: 'recebimento',
    successFeeConditions: '',
    paymentMethod: 'a_vista',
    installmentCount: 1,
    installmentFrequency: 'mensal',
    firstPaymentDate: '',
    latePaymentInterest: '',
    earlyPaymentDiscount: '',
    paymentConditions: '',
    collectionNotes: '',
    proceduralStrategy: '',
    internalNotes: ''
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    procedureType: '',
    agency: '',
    processNumber: '',
    clientId: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientCPF: '',
    priority: 'normal',
    status: 'em_andamento'
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Buscar tipos de procedimento
      const { data: procedureData, error: procedureError } = await (supabase as any)
        .from('administrative_procedure_types')
        .select('*')
        .order('category, name');

      if (procedureError) throw procedureError;

      // Buscar órgãos administrativos
      const { data: agencyData, error: agencyError } = await (supabase as any)
        .from('administrative_agencies')
        .select('*')
        .order('category, name');

      if (agencyError) throw agencyError;

      setProcedureTypes(procedureData || []);
      setAgencies(agencyData || []);
      
      // Buscar clientes
      const { data: clientsData, error: clientsError } = await (supabase as any)
        .from('clients')
        .select('*')
        .order('name');

      if (clientsError) throw clientsError;
      console.log('Clientes carregados:', clientsData); // Debug
      if (clientsData && clientsData.length > 0) {
        console.log('Primeiro cliente:', clientsData[0]);
        console.log('Colunas disponíveis:', Object.keys(clientsData[0]));
      }
      setClients(clientsData || []);
      setFilteredClients(clientsData || []); // Mostrar todos os clientes inicialmente
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados necessários",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  // Preencher formulário quando editando um processo existente
  useEffect(() => {
    if (editingCase) {
      setFormData({
        title: editingCase.title || '',
        description: editingCase.description || '',
        procedureType: editingCase.procedure_type || '',
        agency: editingCase.administrative_agency || '',
        processNumber: editingCase.process_number || '',
        clientId: editingCase.client_id || '',
        clientName: editingCase.client_name || '',
        clientEmail: '',
        clientPhone: '',
        clientCPF: editingCase.client_cpf || '',
        priority: editingCase.priority || 'normal',
        status: editingCase.status || 'em_andamento'
      });
      
      // Carregar dados financeiros se existirem
      if (editingCase.fee_configuration) {
        const config = editingCase.fee_configuration;
        setFinancialInfo({
          clientFees: config.initialFeeAmount ? String(config.initialFeeAmount) : '',
          courtCosts: editingCase.court_costs || '',
          successFeeType: config.successFeeType?.includes('percentage') ? 'percentage' : 'fixed',
          successFeeValue: config.successFeeValue ? String(config.successFeeValue) : '',
          successFeeTrigger: config.successFeeTrigger || 'recebimento',
          successFeeConditions: config.successFeeConditions || '',
          paymentMethod: config.paymentMethod || 'a_vista',
          installmentCount: config.installmentCount || 1,
          installmentFrequency: config.installmentFrequency || 'mensal',
          firstPaymentDate: config.firstPaymentDate || '',
          latePaymentInterest: config.latePaymentInterest ? String(config.latePaymentInterest) : '',
          earlyPaymentDiscount: config.earlyPaymentDiscount ? String(config.earlyPaymentDiscount) : '',
          paymentConditions: config.paymentConditions || '',
          collectionNotes: config.collectionNotes || '',
          proceduralStrategy: editingCase.procedural_strategy || '',
          internalNotes: editingCase.internal_notes || '',
          fee_configuration: config
        });
      } else {
        // Carregar dados financeiros legados se existirem
        setFinancialInfo({
          clientFees: editingCase.client_fees || '',
          courtCosts: editingCase.court_costs || '',
          successFeeType: editingCase.success_fee_type || 'percentage',
          successFeeValue: editingCase.success_fee_value || '',
          successFeeTrigger: 'recebimento',
          successFeeConditions: '',
          paymentMethod: 'a_vista',
          installmentCount: 1,
          installmentFrequency: 'mensal',
          firstPaymentDate: '',
          latePaymentInterest: '',
          earlyPaymentDiscount: '',
          paymentConditions: '',
          collectionNotes: '',
          proceduralStrategy: editingCase.procedural_strategy || '',
          internalNotes: editingCase.internal_notes || ''
        });
      }
    } else {
      // Limpar formulário quando não estiver editando
      setFormData({
        title: '',
        description: '',
        procedureType: '',
        agency: '',
        processNumber: '',
        clientId: '',
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        clientCPF: '',
        priority: 'normal',
        status: 'em_andamento'
      });
      
      // Limpar dados financeiros
      setFinancialInfo({
        clientFees: '',
        courtCosts: '',
        successFeeType: 'percentage',
        successFeeValue: '',
        successFeeTrigger: 'recebimento',
        successFeeConditions: '',
        paymentMethod: 'a_vista',
        installmentCount: 1,
        installmentFrequency: 'mensal',
        firstPaymentDate: '',
        latePaymentInterest: '',
        earlyPaymentDiscount: '',
        paymentConditions: '',
        collectionNotes: '',
        proceduralStrategy: '',
        internalNotes: ''
      });
    }
  }, [editingCase]);



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Erro",
        description: "Usuário não autenticado",
        variant: "destructive"
      });
      return;
    }

    try {
      let processData;
      
      if (editingCase) {
        // Atualizar processo existente
        const { data: updatedData, error: updateError } = await (supabase as any)
          .from('administrative_processes')
          .update({
            title: formData.title,
            description: formData.description,
            procedure_type: formData.procedureType,
            administrative_agency: formData.agency,
            process_number: formData.processNumber,
            client_id: formData.clientId,
            client_name: formData.clientName,
            client_cpf: formData.clientCPF,
            priority: formData.priority,
            status: formData.status,
            client_fees: financialInfo.clientFees || null,
            court_costs: financialInfo.courtCosts || null,
            success_fee_type: financialInfo.successFeeType,
            success_fee_value: financialInfo.successFeeValue || null,
            procedural_strategy: financialInfo.proceduralStrategy,
            internal_notes: financialInfo.internalNotes,
            fee_configuration: financialInfo.fee_configuration || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingCase.id)
          .select()
          .single();

        if (updateError) throw updateError;
        processData = updatedData;
      } else {
        // Criar novo processo administrativo
        const { data: newData, error: insertError } = await (supabase as any)
          .from('administrative_processes')
          .insert({
            title: formData.title,
            description: formData.description,
            procedure_type: formData.procedureType,
            administrative_agency: formData.agency,
            process_number: formData.processNumber,
            client_id: formData.clientId,
            client_name: formData.clientName,
            client_cpf: formData.clientCPF,
            priority: formData.priority,
            status: formData.status,
            client_fees: financialInfo.clientFees || null,
            court_costs: financialInfo.courtCosts || null,
            success_fee_type: financialInfo.successFeeType,
            success_fee_value: financialInfo.successFeeValue || null,
            procedural_strategy: financialInfo.proceduralStrategy,
            internal_notes: financialInfo.internalNotes,
            fee_configuration: financialInfo.fee_configuration || null,
            user_id: user.id,
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (insertError) throw insertError;
        processData = newData;
      }

      // Gerenciar prazos
      if (editingCase) {
        // Remover prazos existentes e inserir os novos
        await (supabase as any)
          .from('administrative_process_deadlines')
          .delete()
          .eq('process_id', editingCase.id);
      }

      if (deadlines.length > 0) {
        const deadlinesToInsert = deadlines.map(deadline => ({
          title: deadline.title,
          description: deadline.description,
          due_date: deadline.due_date,
          priority: deadline.priority,
          type: deadline.type,
          status: deadline.status,
          process_id: processData.id,
          user_id: user.id,
          created_at: new Date().toISOString()
        }));

        const { error: deadlinesError } = await (supabase as any)
          .from('administrative_process_deadlines')
          .insert(deadlinesToInsert);

      if (deadlinesError) throw deadlinesError;
      }

      // 7. Integração Financeira Automática (apenas quando há dados financeiros)
      const hasFinancialData = financialInfo.clientFees || financialInfo.courtCosts || financialInfo.successFeeValue || financialInfo.fee_configuration;
      
      if (hasFinancialData) {
        try {
          if (editingCase) {
            // Verificar se os dados financeiros mudaram antes de atualizar
            const originalFinancialData = {
              client_fees: editingCase.client_fees,
              court_costs: editingCase.court_costs,
              success_fee_value: editingCase.success_fee_value,
              success_fee_type: editingCase.success_fee_type,
              fee_configuration: editingCase.fee_configuration
            };
            
            const newFinancialData = {
              client_fees: financialInfo.clientFees,
              court_costs: financialInfo.courtCosts,
              success_fee_value: financialInfo.successFeeValue,
              success_fee_type: financialInfo.successFeeType,
              fee_configuration: financialInfo.fee_configuration
            };
            
            // Só atualizar se os dados financeiros realmente mudaram
            if (JSON.stringify(originalFinancialData) !== JSON.stringify(newFinancialData)) {
              await AdministrativeFinancialIntegrationService.updateFinancialIntegration(processData.id);
              console.log('✅ Integração financeira atualizada devido a mudanças nos dados!');
            } else {
              console.log('ℹ️ Dados financeiros não mudaram, integração mantida.');
            }
          } else {
            // Criar integração apenas para novos processos com dados financeiros
            await AdministrativeFinancialIntegrationService.createFinancialIntegration(processData.id);
            console.log('✅ Integração financeira criada para novo processo!');
          }
        } catch (financialError) {
          console.error('⚠️ Erro na integração financeira:', financialError);
          // Não falhar o processo principal por erro na integração financeira
          toast({
            title: "Aviso",
            description: "Processo salvo, mas houve problema na integração financeira. Você pode sincronizar manualmente.",
            variant: "default"
          });
        }
      } else {
        console.log('ℹ️ Nenhum dado financeiro configurado, integração não necessária.');
      }

      toast({
        title: "Sucesso",
        description: editingCase ? "Processo administrativo atualizado com sucesso!" : "Processo administrativo criado com sucesso e integrado ao sistema financeiro!"
      });

      // Limpar formulário
      setFormData({
        title: '',
        description: '',
        procedureType: '',
        agency: '',
        processNumber: '',
        clientId: '',
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        clientCPF: '',
        priority: 'normal',
        status: 'em_andamento'
      });
      setDeadlines([]);
        setFinancialInfo({
          clientFees: '',
          courtCosts: '',
          successFeeType: 'percentage',
          successFeeValue: '',
          successFeeTrigger: 'recebimento',
          successFeeConditions: '',
          paymentMethod: 'a_vista',
          installmentCount: 1,
          installmentFrequency: 'mensal',
          firstPaymentDate: '',
          latePaymentInterest: '',
          earlyPaymentDiscount: '',
          paymentConditions: '',
          collectionNotes: '',
          proceduralStrategy: '',
          internalNotes: ''
        });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar processo:', error);
      toast({
        title: "Erro",
        description: editingCase ? "Não foi possível atualizar o processo administrativo" : "Não foi possível criar o processo administrativo",
        variant: "destructive"
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClientSearch = (search: string) => {
    setSearchTerm(search);
    if (!search.trim()) {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client =>
        client.name.toLowerCase().includes(search.toLowerCase()) ||
        client.email.toLowerCase().includes(search.toLowerCase()) ||
        (client.cpf && client.cpf.includes(search)) ||
        (client.document && client.document.includes(search)) ||
        (client.cpf_cnpj && client.cpf_cnpj.includes(search))
      );
      setFilteredClients(filtered);
    }
  };

  const handleInputFocus = () => {
    // Mostrar todos os clientes quando o campo recebe foco
    setFilteredClients(clients);
    setShowClientList(true);
  };

  const handleClientSelect = (clientId: string) => {
    const selectedClient = clients.find(c => c.id === clientId);
    if (selectedClient) {
      console.log('Cliente selecionado:', selectedClient); // Debug
      
      // Tentar diferentes campos para CPF
      const cpfValue = selectedClient.cpf || selectedClient.document || selectedClient.cpf_cnpj || '';
      console.log('CPF encontrado:', cpfValue); // Debug do CPF
      
      setFormData(prev => ({
        ...prev,
        clientId: selectedClient.id,
        clientName: selectedClient.name,
        clientCPF: cpfValue
      }));
      
      console.log('FormData atualizado:', {
        clientId: selectedClient.id,
        clientName: selectedClient.name,
        clientCPF: cpfValue
      }); // Debug do formData
      
      // Ocultar a lista após selecionar um cliente
      setShowClientList(false);
    }
  };

  const openClientModal = () => {
    setIsClientModalOpen(true);
  };

  const addDeadline = (deadline: Omit<Deadline, 'id'>) => {
    const newDeadline: Deadline = {
      ...deadline,
      id: `temp_${Date.now()}` // ID temporário até salvar no banco
    };
    setDeadlines(prev => [...prev, newDeadline]);
    setIsDeadlineModalOpen(false);
    toast({
      title: "Sucesso",
      description: "Prazo adicionado com sucesso!"
    });
  };

  const removeDeadline = (deadlineId: string) => {
    setDeadlines(prev => prev.filter(d => d.id !== deadlineId));
    toast({
      title: "Prazo Removido",
      description: "Prazo removido da lista"
    });
  };

  const handleFinancialChange = (field: keyof FinancialInfo, value: string) => {
    setFinancialInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInputBlur = () => {
    // Ocultar a lista quando sair da caixa de busca
    // Usar setTimeout para permitir clicar nos itens da lista
    setTimeout(() => {
      setShowClientList(false);
    }, 200);
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Processo Administrativo</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando dados...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto bg-white shadow-2xl border border-[#c4ab83]/30">
          <DialogHeader className="relative -m-6 mb-6 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
            {/* Monograma AS como marca d'água */}
            <img
              src="/imgs/logos allana/letra_A_logo.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
            />
            <div className="relative flex items-center gap-4">
              <img
                src="/imgs/logos allana/letra_A_logo.png"
                alt="Allana Sthéfany"
                className="h-12 w-12 shrink-0 select-none object-contain opacity-80"
              />
              <div>
                <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">
                  {editingCase ? 'Editar Processo Administrativo' : 'Novo Processo Administrativo'}
                </DialogTitle>
                <p className="mt-1 text-sm text-slate-500">{editingCase ? 'Edite as informações do processo administrativo' : 'Preencha todas as informações necessárias para criar o processo'}</p>
              </div>
            </div>
          </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="space-y-6"
          onInvalidCapture={(e) => {
            // Campo obrigatório em outra aba: abre a aba para mostrar o campo
            const tab = (e.target as HTMLElement).closest('[data-tab]')?.getAttribute('data-tab');
            if (tab) setActiveTab(tab);
          }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
            <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 sm:grid-cols-6">
              <TabsTrigger value="identification" className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm">Identificação</TabsTrigger>
              <TabsTrigger value="client" className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm">Cliente</TabsTrigger>
              <TabsTrigger value="settings" className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm">Configurações</TabsTrigger>
              <TabsTrigger value="financial" className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm">Financeiro</TabsTrigger>
              <TabsTrigger value="notes" className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm">Observações</TabsTrigger>
              <TabsTrigger value="deadlines" className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm">Prazos</TabsTrigger>
            </TabsList>
          <TabsContent value="identification" forceMount data-tab="identification" className="mt-0 space-y-6 data-[state=inactive]:hidden">
                     {/* Informações Básicas */}
           <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-shadow">
             <CardHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
               <CardTitle className="text-lg font-semibold text-black">Informações Básicas</CardTitle>
             </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 <div>
                   <Label htmlFor="title" className="text-gray-800 font-semibold text-sm">Título do Processo *</Label>
                                     <Input
                     id="title"
                     value={formData.title}
                     onChange={(e) => handleInputChange('title', e.target.value)}
                     placeholder="Ex: Aposentadoria por Idade"
                     required
                     className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                   />
                </div>
                                 <div>
                   <Label htmlFor="processNumber" className="text-gray-800 font-semibold text-sm">Número do Processo</Label>
                   <Input
                     id="processNumber"
                     value={formData.processNumber}
                     onChange={(e) => handleInputChange('processNumber', e.target.value)}
                     placeholder="Ex: 12345/2024"
                     className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                   />
                 </div>
              </div>

                             <div>
                 <Label htmlFor="description" className="text-gray-800 font-semibold text-sm">Descrição</Label>
                 <Textarea
                   id="description"
                   value={formData.description}
                   onChange={(e) => handleInputChange('description', e.target.value)}
                   placeholder="Descreva os detalhes do processo..."
                   rows={3}
                   className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                 />
               </div>
            </CardContent>
          </Card>

                     {/* Tipo de Procedimento e Órgão */}
           <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-shadow">
             <CardHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
               <CardTitle className="text-lg font-semibold text-black">Classificação</CardTitle>
             </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 <div>
                   <Label htmlFor="procedureType" className="text-gray-800 font-semibold text-sm">Tipo de Procedimento *</Label>
                  <Select
                    value={formData.procedureType}
                    onValueChange={(value) => handleInputChange('procedureType', value)}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {procedureTypes.length > 0 ? (
                        Object.entries(
                          procedureTypes.reduce((acc, type) => {
                            if (!acc[type.category]) acc[type.category] = [];
                            acc[type.category].push(type);
                            return acc;
                          }, {} as Record<string, ProcedureType[]>)
                        ).map(([category, types]) => (
                          <SelectGroup key={category}>
                            <SelectLabel className="font-semibold text-blue-700">
                              {category}
                            </SelectLabel>
                            {types.map((type) => (
                              <SelectItem key={type.id} value={type.name}>
                                {type.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))
                      ) : (
                        <SelectItem value="no-data" disabled>
                          Nenhum tipo disponível
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                                 <div>
                   <Label htmlFor="agency" className="text-gray-800 font-semibold text-sm">Órgão Administrativo *</Label>
                  <Select
                    value={formData.agency}
                    onValueChange={(value) => handleInputChange('agency', value)}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o órgão" />
                    </SelectTrigger>
                    <SelectContent>
                      {agencies.length > 0 ? (
                        Object.entries(
                          agencies.reduce((acc, agency) => {
                            if (!acc[agency.category]) acc[agency.category] = [];
                            acc[agency.category].push(agency);
                            return acc;
                          }, {} as Record<string, Agency[]>)
                        ).map(([category, agencyList]) => (
                          <SelectGroup key={category}>
                            <SelectLabel className="font-semibold text-blue-700">
                              {category}
                            </SelectLabel>
                            {agencyList.map((agency) => (
                              <SelectItem key={agency.id} value={agency.name}>
                                {agency.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))
                      ) : (
                        <SelectItem value="no-data" disabled>
                          Nenhum órgão disponível
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          </TabsContent>
          <TabsContent value="client" forceMount data-tab="client" className="mt-0 space-y-6 data-[state=inactive]:hidden">
                     {/* Informações do Cliente */}
           <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-shadow">
             <CardHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
               <CardTitle className="text-lg font-semibold text-black">Informações do Cliente</CardTitle>
             </CardHeader>
            <CardContent className="space-y-4">
                             {/* Busca de Cliente */}
                               <div>
                  <Label className="text-gray-800 font-semibold text-sm">Buscar Cliente Existente</Label>
                 <div className="flex gap-2 mt-2">
                   <div className="text-sm text-gray-500 mb-2">
                     {filteredClients.length > 0 ? (
                       `Mostrando ${filteredClients.length} cliente${filteredClients.length > 1 ? 's' : ''}`
                     ) : (
                       'Nenhum cliente encontrado'
                     )}
                   </div>
                 </div>
                 <div className="flex gap-2 mt-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                         <Input
                       placeholder="Digite nome, email ou CPF..."
                       value={searchTerm}
                       onChange={(e) => handleClientSearch(e.target.value)}
                       onFocus={handleInputFocus}
                       onBlur={handleInputBlur}
                       className="pl-10 bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                     />
                     {searchTerm && (
                       <Button
                         type="button"
                         variant="ghost"
                         size="sm"
                         onClick={() => {
                           setSearchTerm('');
                           setFilteredClients(clients);
                           setShowClientList(false);
                         }}
                         className="absolute right-2 top-1/2 transform -translate-y-1/2"
                       >
                         <X className="w-4 h-4" />
                       </Button>
                     )}
                  </div>
                  <Button
                    type="button"
                    onClick={openClientModal}
                    variant="outline"
                    className="whitespace-nowrap"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Novo Cliente
                  </Button>
                </div>
                
                                 {/* Lista de Clientes Filtrados */}
                 {showClientList && filteredClients.length > 0 && (
                   <div className="mt-3 max-h-40 overflow-y-auto border rounded-lg">
                     {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        onClick={() => handleClientSelect(client.id)}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex items-center justify-between"
                      >
                                                 <div>
                           <div className="font-medium">{client.name}</div>
                           <div className="text-sm text-gray-600">{client.email}</div>
                           {(client.cpf || client.document || client.cpf_cnpj) && (
                             <div className="text-xs text-gray-500">
                               Doc: {client.cpf || client.document || client.cpf_cnpj}
                             </div>
                           )}
                         </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClientSelect(client.id);
                          }}
                        >
                          Selecionar
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Cliente Selecionado */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 <div>
                   <Label htmlFor="clientName" className="text-gray-800 font-semibold text-sm">Nome do Cliente *</Label>
                                     <Input
                     id="clientName"
                     value={formData.clientName}
                     onChange={(e) => handleInputChange('clientName', e.target.value)}
                     placeholder="Nome completo"
                     required
                     className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                   />
                </div>
                                                   <div>
                    <Label htmlFor="clientCPF" className="text-gray-800 font-semibold text-sm">CPF do Cliente</Label>
                    <Input
                      id="clientCPF"
                      value={formData.clientCPF}
                      onChange={(e) => handleInputChange('clientCPF', e.target.value)}
                      placeholder="000.000.000-00"
                      className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                    />
                   {/* Debug visual */}
                   <div className="text-xs text-gray-500 mt-1">
                     Valor atual: "{formData.clientCPF || 'vazio'}"
                   </div>
                 </div>
              </div>
            </CardContent>
          </Card>

          </TabsContent>
          <TabsContent value="settings" forceMount data-tab="settings" className="mt-0 space-y-6 data-[state=inactive]:hidden">
                     {/* Prioridade e Status */}
           <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-shadow">
             <CardHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
               <CardTitle className="text-lg font-semibold text-black">Configurações</CardTitle>
             </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="priority" className="text-gray-800 font-semibold text-sm">Prioridade</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => handleInputChange('priority', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baixa">Baixa</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                      <SelectItem value="urgente">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="status" className="text-gray-800 font-semibold text-sm">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleInputChange('status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="em-andamento">Em Andamento</SelectItem>
                      <SelectItem value="aguardando-documentos">Aguardando Documentos</SelectItem>
                      <SelectItem value="concluido">Concluído</SelectItem>
                      <SelectItem value="suspenso">Suspenso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          </TabsContent>
          <TabsContent value="financial" forceMount data-tab="financial" className="mt-0 space-y-6 data-[state=inactive]:hidden">
          {/* Configuração Avançada de Cobranças */}
          <AdvancedFeeConfiguration
            initialConfig={financialInfo.fee_configuration}
            onConfigChange={(config) => handleFinancialChange('fee_configuration', config)}
          />

          </TabsContent>
          <TabsContent value="notes" forceMount data-tab="notes" className="mt-0 space-y-6 data-[state=inactive]:hidden">
          {/* Estratégia e Observações */}
          <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
              <CardTitle className="text-lg font-semibold text-black">📋 Estratégia e Observações</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="proceduralStrategy" className="text-gray-800 font-semibold text-sm">Estratégia Administrativa</Label>
                <Textarea
                  id="proceduralStrategy"
                  value={financialInfo.proceduralStrategy}
                  onChange={(e) => handleFinancialChange('proceduralStrategy', e.target.value)}
                  placeholder="Descreva a estratégia administrativa, argumentos principais, precedentes administrativos relevantes..."
                  rows={3}
                  className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <Label htmlFor="internalNotes" className="text-gray-800 font-semibold text-sm">Observações Internas</Label>
                <Textarea
                  id="internalNotes"
                  value={financialInfo.internalNotes}
                  onChange={(e) => handleFinancialChange('internalNotes', e.target.value)}
                  placeholder="Observações internas sobre o caso, parcerias, etc..."
                  rows={3}
                  className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500"
                />
              </div>
            </CardContent>
          </Card>

          </TabsContent>
          <TabsContent value="deadlines" forceMount data-tab="deadlines" className="mt-0 space-y-6 data-[state=inactive]:hidden">
                       {/* Prazos */}
            <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
                <CardTitle className="text-lg font-semibold text-black">Prazos e Datas Importantes</CardTitle>
               <Button
                 type="button"
                 onClick={() => setIsDeadlineModalOpen(true)}
                 variant="outline"
                 size="sm"
                 className="flex items-center gap-2"
               >
                 <Plus className="w-4 h-4" />
                 Adicionar Prazo
               </Button>
             </CardHeader>
             <CardContent>
               {deadlines.length === 0 ? (
                 <div className="text-center py-6 text-gray-500">
                   <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                   <p>Nenhum prazo definido</p>
                   <p className="text-sm">Clique em "Adicionar Prazo" para definir datas importantes</p>
                 </div>
               ) : (
                 <div className="space-y-3">
                   {deadlines.map((deadline) => (
                     <div
                       key={deadline.id}
                       className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
                     >
                       <div className="flex-1">
                         <div className="flex items-center gap-2 mb-1">
                           <Clock className="w-4 h-4 text-blue-600" />
                           <span className="font-medium">{deadline.title}</span>
                           <Badge
                             variant={
                               deadline.priority === 'urgente' ? 'destructive' :
                               deadline.priority === 'alta' ? 'default' :
                               deadline.priority === 'normal' ? 'secondary' : 'outline'
                             }
                           >
                             {deadline.priority}
                           </Badge>
                           <Badge variant="outline">{deadline.type.replace('_', ' ')}</Badge>
                         </div>
                         <p className="text-sm text-gray-600 mb-1">{deadline.description}</p>
                         <div className="flex items-center gap-4 text-xs text-gray-500">
                           <span>Vencimento: {new Date(deadline.due_date).toLocaleDateString('pt-BR')}</span>
                           <span>Status: {deadline.status}</span>
                         </div>
                       </div>
                       <Button
                         type="button"
                         onClick={() => removeDeadline(deadline.id)}
                         variant="ghost"
                         size="sm"
                         className="text-red-600 hover:text-red-700 hover:bg-red-50"
                       >
                         <Trash2 className="w-4 h-4" />
                       </Button>
                     </div>
                   ))}
                 </div>
               )}
             </CardContent>
           </Card>

          </TabsContent>
          </Tabs>

                      {/* Botões */}
           <div className="flex justify-end space-x-3 pt-6 pb-4 bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 -m-6 px-6 border-t border-[#c4ab83]/50">
             <Button
               type="button"
               variant="outline"
               onClick={onClose}
               className="px-8 py-3 text-gray-700 border-gray-300 hover:bg-gray-100 hover:border-gray-400"
             >
               Cancelar
             </Button>
             <Button
                type="submit"
                className="bg-[#c4ab83] hover:bg-[#b39a72] px-8 py-3 text-slate-900 font-semibold shadow-sm transition-all"
              >
                {editingCase ? 'Salvar Alterações' : 'Criar Processo'}
              </Button>
           </div>
        </form>
      </DialogContent>
    </Dialog>

             {/* Modal para Cadastrar Novo Cliente */}
       <Dialog open={isClientModalOpen} onOpenChange={setIsClientModalOpen}>
         <DialogContent className="max-w-md bg-white shadow-2xl border-2 border-[#c4ab83]/30">
                     <DialogHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 p-4 -m-4 mb-4 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
             <DialogTitle className="text-xl font-bold text-black">Cadastrar Novo Cliente</DialogTitle>
           </DialogHeader>
          <div className="space-y-4">
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const newClient = {
                name: formData.get('name') as string,
                email: formData.get('email') as string,
                cpf: formData.get('cpf') as string,
              };
              
              try {
                const { data, error } = await (supabase as any)
                  .from('clients')
                  .insert([newClient])
                  .select()
                  .single();

                if (error) throw error;

                const createdClient: Client = {
                  id: data.id,
                  name: data.name,
                  email: data.email,
                  cpf: data.cpf
                };

                setClients(prev => [...prev, createdClient]);
                setFilteredClients(prev => [...prev, createdClient]);
                handleClientSelect(createdClient.id);
                setIsClientModalOpen(false);
                toast({
                  title: "Sucesso",
                  description: "Cliente cadastrado com sucesso!"
                });
              } catch (error) {
                console.error('Erro ao cadastrar cliente:', error);
                toast({
                  title: "Erro",
                  description: "Não foi possível cadastrar o cliente",
                  variant: "destructive"
                });
              }
            }}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome *</Label>
                  <Input id="name" name="name" required placeholder="Nome completo" />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" name="email" type="email" required placeholder="email@exemplo.com" />
                </div>
                <div>
                  <Label htmlFor="cpf">CPF</Label>
                  <Input id="cpf" name="cpf" placeholder="000.000.000-00" />
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsClientModalOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit">
                    Cadastrar Cliente
                  </Button>
                </div>
              </div>
            </form>
          </div>
                 </DialogContent>
       </Dialog>

               {/* Modal para Adicionar Prazo */}
        <Dialog open={isDeadlineModalOpen} onOpenChange={setIsDeadlineModalOpen}>
          <DialogContent className="max-w-md bg-white shadow-2xl border-2 border-gray-200">
                       <DialogHeader className="bg-gradient-to-r from-green-50 to-blue-50 p-4 -m-4 mb-4 border-b border-green-200">
              <DialogTitle className="text-xl font-bold text-green-800">Adicionar Novo Prazo</DialogTitle>
            </DialogHeader>
           <div className="space-y-4">
             <form onSubmit={(e) => {
               e.preventDefault();
               const formData = new FormData(e.currentTarget);
               const newDeadline = {
                 title: formData.get('title') as string,
                 description: formData.get('description') as string,
                 due_date: formData.get('due_date') as string,
                 priority: formData.get('priority') as 'baixa' | 'normal' | 'alta' | 'urgente',
                 type: formData.get('type') as 'prazo_legal' | 'prazo_interno' | 'prazo_cliente' | 'outro',
                 status: 'pendente' as const
               };
               
               addDeadline(newDeadline);
             }}>
               <div className="space-y-4">
                 <div>
                   <Label htmlFor="title">Título do Prazo *</Label>
                   <Input id="title" name="title" required placeholder="Ex: Prazo para contestação" />
                 </div>
                 <div>
                   <Label htmlFor="description">Descrição</Label>
                   <Textarea id="description" name="description" placeholder="Detalhes sobre o prazo..." rows={2} />
                 </div>
                 <div>
                   <Label htmlFor="due_date">Data de Vencimento *</Label>
                   <Input id="due_date" name="due_date" type="date" required />
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <Label htmlFor="priority">Prioridade</Label>
                     <Select name="priority" defaultValue="normal">
                       <SelectTrigger>
                         <SelectValue />
                       </SelectTrigger>
                       <SelectContent>
                         <SelectItem value="baixa">Baixa</SelectItem>
                         <SelectItem value="normal">Normal</SelectItem>
                         <SelectItem value="alta">Alta</SelectItem>
                         <SelectItem value="urgente">Urgente</SelectItem>
                       </SelectContent>
                     </Select>
                   </div>
                   <div>
                     <Label htmlFor="type">Tipo de Prazo</Label>
                     <Select name="type" defaultValue="prazo_legal">
                       <SelectTrigger>
                         <SelectValue />
                       </SelectTrigger>
                       <SelectContent>
                         <SelectItem value="prazo_legal">Prazo Legal</SelectItem>
                         <SelectItem value="prazo_interno">Prazo Interno</SelectItem>
                         <SelectItem value="prazo_cliente">Prazo Cliente</SelectItem>
                         <SelectItem value="outro">Outro</SelectItem>
                       </SelectContent>
                     </Select>
                   </div>
                 </div>
                 <div className="flex justify-end space-x-2 pt-4">
                   <Button type="button" variant="outline" onClick={() => setIsDeadlineModalOpen(false)}>
                     Cancelar
                   </Button>
                   <Button type="submit">
                     Adicionar Prazo
                   </Button>
                 </div>
               </div>
             </form>
           </div>
         </DialogContent>
       </Dialog>
    </>
   );
 }
